import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { 
  Menu, Search, Mail, Phone, DollarSign, Send, Trash2,
  FileText, Clock, CheckCircle, XCircle, MessageSquare, CalendarPlus, UserCheck, Edit,
  Calendar, CreditCard, Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";

const API = "/api";

const Quotes = () => {
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [isEditingQuote, setIsEditingQuote] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deletingQuote, setDeletingQuote] = useState(false);
  const [pendingQuotes, setPendingQuotes] = useState(0);
  const [quoteForm, setQuoteForm] = useState({
    oneTimePrice: "",
    monthly1xPrice: "",
    monthly2xPrice: "",
    yearly1xPrice: "",
    yearly2xPrice: "",
    message: "",
    sendEmail: true,
    sendSms: true,
  });
  
  // Convert to Job state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertingQuote, setConvertingQuote] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [newQuote, setNewQuote] = useState({
    label: "", quoted_price: "", monthly_1x_price: "", monthly_2x_price: "",
    yearly_1x_price: "", yearly_2x_price: "", yard_size: "medium", grass_height: "medium", notes: "",
  });
  const [convertForm, setConvertForm] = useState({
    scheduledDate: "",
    scheduledTime: "09:00",
    durationMinutes: 60,
    assignedStaffId: "",
    notes: "",
  });

  useEffect(() => {
    fetchQuotes();
    fetchStaff();
    fetchSettings();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`, { withCredentials: true });
      setQuotes(response.data);
    } catch (error) {
      console.error("Failed to fetch quotes:", error);
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`, { withCredentials: true });
      setStaffList(response.data);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      setSubscriptionsEnabled(response.data.subscriptions_enabled !== false);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  const handleCreateQuote = async () => {
    setCreatingQuote(true);
    try {
      const payload = {
        label: newQuote.label || undefined,
        quoted_price: newQuote.quoted_price ? parseFloat(newQuote.quoted_price) : undefined,
        monthly_1x_price: newQuote.monthly_1x_price ? parseFloat(newQuote.monthly_1x_price) : undefined,
        monthly_2x_price: newQuote.monthly_2x_price ? parseFloat(newQuote.monthly_2x_price) : undefined,
        yearly_1x_price: newQuote.yearly_1x_price ? parseFloat(newQuote.yearly_1x_price) : undefined,
        yearly_2x_price: newQuote.yearly_2x_price ? parseFloat(newQuote.yearly_2x_price) : undefined,
        yard_size: newQuote.yard_size || "medium",
        grass_height: newQuote.grass_height || "medium",
        notes: newQuote.notes || undefined,
      };
      await axios.post(`${API}/admin/quotes/quick-create`, payload, { withCredentials: true });
      toast.success("Quote created!");
      setCreateDialogOpen(false);
      setNewQuote({ label: "", quoted_price: "", monthly_1x_price: "", monthly_2x_price: "", yearly_1x_price: "", yearly_2x_price: "", yard_size: "medium", grass_height: "medium", notes: "" });
      fetchQuotes();
    } catch {
      toast.error("Failed to create quote");
    } finally {
      setCreatingQuote(false);
    }
  };

  const openSendDialog = (quote, isEditing = false) => {
    setSelectedQuote(quote);
    setIsEditingQuote(isEditing);
    setQuoteForm({
      oneTimePrice: quote.quoted_price?.toString() || "",
      monthly1xPrice: quote.monthly_1x_price?.toString() || "",
      monthly2xPrice: quote.monthly_2x_price?.toString() || "",
      yearly1xPrice: quote.yearly_1x_price?.toString() || "",
      yearly2xPrice: quote.yearly_2x_price?.toString() || "",
      message: "",
      sendEmail: !isEditing,
      sendSms: !isEditing,
    });
    setSendDialogOpen(true);
  };

  const openConvertDialog = (quote) => {
    if (!quote.quoted_price) {
      toast.error("Please send a quote with a price first before converting to a job");
      return;
    }
    setSelectedQuote(quote);
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setConvertForm({
      scheduledDate: tomorrow.toISOString().split('T')[0],
      scheduledTime: "09:00",
      durationMinutes: 60,
      assignedStaffId: "",
      notes: "",
    });
    setConvertDialogOpen(true);
  };

  const handleConvertToJob = async () => {
    if (!convertForm.scheduledDate) {
      toast.error("Please select a date");
      return;
    }
    if (!convertForm.scheduledTime) {
      toast.error("Please select a time");
      return;
    }

    setConvertingQuote(true);
    try {
      const response = await axios.post(`${API}/quotes/${selectedQuote.quote_id}/convert`, {
        scheduled_date: convertForm.scheduledDate,
        scheduled_time: convertForm.scheduledTime,
        duration_minutes: parseInt(convertForm.durationMinutes),
        assigned_staff_id: convertForm.assignedStaffId || null,
        notes: convertForm.notes || null,
      }, { withCredentials: true });

      toast.success(`Job scheduled for ${format(parseISO(convertForm.scheduledDate), "MMM d, yyyy")} at ${convertForm.scheduledTime}!`);
      setConvertDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      console.error("Failed to convert quote:", error);
      toast.error(error.response?.data?.detail || "Failed to convert quote to job");
    } finally {
      setConvertingQuote(false);
    }
  };

  const handleSendQuote = async () => {
    if (!quoteForm.oneTimePrice || parseFloat(quoteForm.oneTimePrice) <= 0) {
      toast.error("Please enter a valid one-time price");
      return;
    }

    setSendingQuote(true);
    try {
      const response = await axios.post(`${API}/quotes/send`, {
        quote_id: selectedQuote.quote_id,
        price: parseFloat(quoteForm.oneTimePrice),
        monthly_1x_price: quoteForm.monthly1xPrice ? parseFloat(quoteForm.monthly1xPrice) : null,
        monthly_2x_price: quoteForm.monthly2xPrice ? parseFloat(quoteForm.monthly2xPrice) : null,
        yearly_1x_price: quoteForm.yearly1xPrice ? parseFloat(quoteForm.yearly1xPrice) : null,
        yearly_2x_price: quoteForm.yearly2xPrice ? parseFloat(quoteForm.yearly2xPrice) : null,
        message: quoteForm.message || null,
        send_email: quoteForm.sendEmail,
        send_sms: quoteForm.sendSms,
      }, { withCredentials: true });

      const results = response.data.results;
      let successMsg = "Quote sent";
      if (results.email_sent && results.sms_sent) {
        successMsg = "Quote sent via email and SMS!";
      } else if (results.email_sent) {
        successMsg = "Quote sent via email!";
      } else if (results.sms_sent) {
        successMsg = "Quote sent via SMS!";
      } else {
        successMsg = "Quote marked as sent (notifications not configured)";
      }

      toast.success(successMsg);
      setSendDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      console.error("Failed to send quote:", error);
      toast.error("Failed to send quote");
    } finally {
      setSendingQuote(false);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    setQuoteToDelete(quoteId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteQuote = async () => {
    if (!quoteToDelete) return;
    
    setDeletingQuote(true);
    try {
      await axios.delete(`${API}/quotes/${quoteToDelete}`, { withCredentials: true });
      toast.success("Quote deleted");
      fetchQuotes();
    } catch (error) {
      console.error("Failed to delete quote:", error);
      toast.error("Failed to delete quote");
    } finally {
      setDeletingQuote(false);
      setDeleteDialogOpen(false);
      setQuoteToDelete(null);
    }
  };

  const handleMarkAsPaid = async (quoteId) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}`, 
        { status: "paid" }, 
        { withCredentials: true }
      );
      toast.success("Quote marked as paid!");
      fetchQuotes();
    } catch (error) {
      console.error("Failed to mark quote as paid:", error);
      toast.error("Failed to update quote status");
    }
  };

  // Activate Customer dialog state
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activateQuote, setActivateQuote] = useState(null);
  const [activating, setActivating] = useState(false);

  const openActivateDialog = (quote) => {
    setActivateQuote(quote);
    setActivateDialogOpen(true);
  };

  const handleActivateCustomer = async (serviceType) => {
    if (!activateQuote) return;
    setActivating(true);
    try {
      // Mark quote as paid
      await axios.put(`${API}/quotes/${activateQuote.quote_id}`, 
        { status: "paid" }, 
        { withCredentials: true }
      );
      
      // If subscription type, activate the subscription
      if (serviceType !== "single_cut") {
        const planMap = { 
          monthly_1x: "monthly", monthly_2x: "monthly", 
          yearly_1x: "yearly", yearly_2x: "yearly" 
        };
        const freqMap = {
          monthly_1x: 1, monthly_2x: 2,
          yearly_1x: 1, yearly_2x: 2
        };
        await axios.post(`${API}/subscriptions`, {
          customer_id: activateQuote.customer_id || activateQuote.user_id,
          plan: planMap[serviceType],
          frequency: freqMap[serviceType],
        }, { withCredentials: true });
      }
      
      toast.success(serviceType === "single_cut" 
        ? "Customer activated for single cut!" 
        : `Customer subscribed to ${serviceType.replace("_", " ")}!`);
      setActivateDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      console.error("Failed to activate customer:", error);
      toast.error(error.response?.data?.detail || "Failed to activate customer");
    } finally {
      setActivating(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "sent":
        return <span className="px-3 py-1 bg-blue-400/20 text-blue-400 rounded-full text-xs flex items-center gap-1"><Send className="w-3 h-3" /> {t("quotes.sent", "Sent")}</span>;
      case "paid":
        return <span className="px-3 py-1 bg-[#39FF14]/20 text-[#39FF14] rounded-full text-xs flex items-center gap-1"><CreditCard className="w-3 h-3" /> {t("quotes.paid", "Paid")}</span>;
      case "accepted":
        return <span className="px-3 py-1 bg-[#39FF14]/20 text-[#39FF14] rounded-full text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {t("quotes.accepted", "Accepted")}</span>;
      case "declined":
        return <span className="px-3 py-1 bg-red-400/20 text-red-400 rounded-full text-xs flex items-center gap-1"><XCircle className="w-3 h-3" /> {t("quotes.declined", "Declined")}</span>;
      case "converted":
        return <span className="px-3 py-1 bg-purple-400/20 text-purple-400 rounded-full text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> {t("quotes.converted", "Converted")}</span>;
      default:
        return <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> {t("quotes.awaiting", "Awaiting Quote")}</span>;
    }
  };

  const yardSizeLabels = {
    small: t("booking.yardSize.small", "0-100 m² (Small)"),
    medium: t("booking.yardSize.medium", "100-300 m² (Medium)"),
    large: t("booking.yardSize.large", "300-500 m² (Large)"),
    xlarge: t("booking.yardSize.xlarge", "500+ m² (Extra Large)"),
    "extra-large": t("booking.yardSize.xlarge", "500+ m² (Extra Large)"),
    unknown: "Unknown",
  };

  const grassHeightLabels = {
    short: t("booking.grassHeight.short", "Short (<5cm)"),
    medium: t("booking.grassHeight.medium", "Medium (5-15cm)"),
    tall: t("booking.grassHeight.tall", "Tall (15-30cm)"),
    overgrown: t("booking.grassHeight.overgrown", "Very Tall (30cm+)"),
    unknown: "Unknown",
  };

  const filteredQuotes = quotes.filter((q) =>
    q.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.customer_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.status?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Three sections: Awaiting Quote, Pending Payment, Processed
  const awaitingQuotes = filteredQuotes.filter(q => q.status === "pending");
  const pendingPaymentQuotes = filteredQuotes.filter(q => q.status === "sent");
  const processedQuotes = filteredQuotes.filter(q => ["converted", "accepted", "declined", "paid"].includes(q.status));

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen}
        pendingQuotes={awaitingQuotes.length}
      />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase">{t("quotes.title", "Quote Requests")}</h1>
                <p className="text-[#A1A1AA] text-sm">{quotes.length} {t("quotes.total", "total")} • {awaitingQuotes.length} {t("quotes.awaitingCount", "awaiting")} • {pendingPaymentQuotes.length} {t("quotes.pendingCount", "pending payment")}</p>
              </div>
            </div>
            <Button
              className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
              onClick={() => setCreateDialogOpen(true)}
              data-testid="create-quote-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Quote
            </Button>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
            <Input
              data-testid="search-quotes-input"
              placeholder={t("quotes.searchPlaceholder", "Search quotes by name, email, or status...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14] max-w-md"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">{t("quotes.noQuotes", "No quote requests")}</h3>
              <p className="text-[#A1A1AA]">{t("quotes.noQuotesDesc", "Quote requests from customers will appear here")}</p>
            </div>
          ) : (
            <>
              {/* Awaiting Quote Section */}
              {awaitingQuotes.length > 0 && (
                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-400" />
                    {t("quotes.awaiting", "Awaiting Quote")} ({awaitingQuotes.length})
                  </h2>
                  <div className="grid gap-4">
                    {awaitingQuotes.map((quote) => (
                      <div
                        key={quote.quote_id}
                        data-testid={`quote-card-${quote.quote_id}`}
                        className="bg-[#121212] border-2 border-yellow-400/30 rounded-xl p-6 hover:border-yellow-400/50 transition-all"
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="text-white font-semibold text-lg">{quote.customer_name}</h3>
                                <div className="flex flex-wrap gap-3 mt-1 text-[#A1A1AA] text-sm">
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-4 h-4" />
                                    {quote.customer_email}
                                  </span>
                                  {quote.customer_phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-4 h-4" />
                                      {quote.customer_phone}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {getStatusBadge(quote.status)}
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-[#0A0A0A] rounded-lg">
                              <div>
                                <p className="text-[#A1A1AA] text-xs uppercase mb-1">{t("quotes.yardSize", "Yard Size")}</p>
                                <p className="text-white font-medium">{yardSizeLabels[quote.yard_size] || quote.yard_size}</p>
                              </div>
                              <div>
                                <p className="text-[#A1A1AA] text-xs uppercase mb-1">{t("quotes.grassHeight", "Grass Height")}</p>
                                <p className="text-white font-medium">{grassHeightLabels[quote.grass_height] || quote.grass_height}</p>
                              </div>
                              {quote.customer_address && (
                                <div className="col-span-2">
                                  <p className="text-[#A1A1AA] text-xs uppercase mb-1">{t("quotes.address", "Address")}</p>
                                  <p className="text-white font-medium">{quote.customer_address}</p>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-4 mt-4">
                              <span className="text-[#A1A1AA] text-xs">
                                {t("quotes.requested", "Requested")} {quote.created_at ? format(parseISO(quote.created_at), "MMM d, yyyy 'at' h:mm a") : ""}
                              </span>
                            </div>
                          </div>

                          <div className="flex flex-row lg:flex-col gap-2">
                            <Button
                              data-testid={`send-quote-${quote.quote_id}`}
                              onClick={() => openSendDialog(quote)}
                              className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold flex-1 lg:flex-none"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              {t("quotes.sendQuote", "Send Quote")}
                            </Button>
                            <Button
                              data-testid={`delete-quote-${quote.quote_id}`}
                              onClick={() => handleDeleteQuote(quote.quote_id)}
                              variant="outline"
                              className="border-red-400/50 text-red-400 hover:bg-red-400/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Pending Payment Section - Quotes sent but awaiting customer payment */}
              {pendingPaymentQuotes.length > 0 && (
                <section className="mb-8">
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-400" />
                    {t("quotes.pending", "Pending Payment")} ({pendingPaymentQuotes.length})
                  </h2>
                  <div className="grid gap-4">
                    {pendingPaymentQuotes.map((quote) => (
                      <div
                        key={quote.quote_id}
                        data-testid={`pending-payment-${quote.quote_id}`}
                        className="bg-[#121212] border-2 border-blue-400/30 rounded-xl p-6 hover:border-blue-400/50 transition-all"
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="text-white font-semibold text-lg">{quote.customer_name}</h3>
                                <div className="flex flex-wrap gap-3 mt-1 text-[#A1A1AA] text-sm">
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-4 h-4" />
                                    {quote.customer_email}
                                  </span>
                                  {quote.customer_phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-4 h-4" />
                                      {quote.customer_phone}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-400/10 text-blue-400 border border-blue-400/30">
                                <Send className="w-3 h-3" />
                                {t("quotes.quoteSent", "Quote Sent")}
                              </span>
                            </div>
                            
                            {/* Pricing Display */}
                            <div className="p-4 bg-[#0A0A0A] rounded-lg mt-4">
                              <p className="text-[#A1A1AA] text-xs uppercase mb-3">{t("quotes.quotedPrices", "Quoted Prices")}</p>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-3 bg-[#121212] rounded-lg border border-[#39FF14]/30">
                                  <p className="text-[#A1A1AA] text-xs mb-1">{t("quotes.oneTimeCut", "One-Time Cut")}</p>
                                  <p className="text-[#39FF14] font-bold text-lg">${quote.quoted_price?.toFixed(2) || "0.00"}</p>
                                </div>
                                {quote.monthly_1x_price && (
                                  <div className="p-3 bg-[#121212] rounded-lg border border-blue-400/30">
                                    <p className="text-[#A1A1AA] text-xs mb-1">Monthly (1x/mo)</p>
                                    <p className="text-blue-400 font-bold text-lg">${quote.monthly_1x_price?.toFixed(2)}/mo</p>
                                  </div>
                                )}
                                {quote.monthly_2x_price && (
                                  <div className="p-3 bg-[#121212] rounded-lg border border-blue-400/30">
                                    <p className="text-[#A1A1AA] text-xs mb-1">Monthly (2x/mo)</p>
                                    <p className="text-blue-300 font-bold text-lg">${quote.monthly_2x_price?.toFixed(2)}/mo</p>
                                  </div>
                                )}
                                {quote.yearly_1x_price && (
                                  <div className="p-3 bg-[#121212] rounded-lg border border-yellow-400/30">
                                    <p className="text-[#A1A1AA] text-xs mb-1">Yearly (1x/mo)</p>
                                    <p className="text-yellow-400 font-bold text-lg">${(quote.yearly_1x_price / 12).toFixed(2)}/mo <span className="text-xs font-normal">{t("quotes.forAYear", "for a year")}</span></p>
                                  </div>
                                )}
                                {quote.yearly_2x_price && (
                                  <div className="p-3 bg-[#121212] rounded-lg border border-yellow-400/30">
                                    <p className="text-[#A1A1AA] text-xs mb-1">Yearly (2x/mo)</p>
                                    <p className="text-yellow-300 font-bold text-lg">${(quote.yearly_2x_price / 12).toFixed(2)}/mo <span className="text-xs font-normal">{t("quotes.forAYear", "for a year")}</span></p>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-[#0A0A0A] rounded-lg">
                              <div>
                                <p className="text-[#A1A1AA] text-xs uppercase mb-1">Yard Size</p>
                                <p className="text-white font-medium">{yardSizeLabels[quote.yard_size] || quote.yard_size}</p>
                              </div>
                              <div>
                                <p className="text-[#A1A1AA] text-xs uppercase mb-1">Grass Height</p>
                                <p className="text-white font-medium">{grassHeightLabels[quote.grass_height] || quote.grass_height}</p>
                              </div>
                              {quote.customer_address && (
                                <div className="col-span-2">
                                  <p className="text-[#A1A1AA] text-xs uppercase mb-1">Address</p>
                                  <p className="text-white font-medium">{quote.customer_address}</p>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-4 mt-4">
                              <span className="text-[#A1A1AA] text-xs">
                                Sent {quote.quote_sent_at ? format(parseISO(quote.quote_sent_at), "MMM d, yyyy 'at' h:mm a") : ""}
                                {quote.quote_sent_via && ` via ${quote.quote_sent_via}`}
                              </span>
                            </div>
                          </div>

                          <div className="flex flex-row lg:flex-col gap-2">
                            <Button
                              data-testid={`activate-customer-${quote.quote_id}`}
                              onClick={() => openActivateDialog(quote)}
                              className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold flex-1 lg:flex-none"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Activate Customer
                            </Button>
                            <Button
                              data-testid={`convert-quote-${quote.quote_id}`}
                              onClick={() => openConvertDialog(quote)}
                              className="bg-purple-500 hover:bg-purple-600 text-white flex-1 lg:flex-none"
                            >
                              <CalendarPlus className="w-4 h-4 mr-2" />
                              {t("quotes.scheduleJob", "Schedule Job")}
                            </Button>
                            <Button
                              data-testid={`edit-quote-${quote.quote_id}`}
                              onClick={() => openSendDialog(quote, true)}
                              variant="outline"
                              className="border-blue-400/50 text-blue-400 hover:bg-blue-400/10"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              {t("common.edit", "Edit")}
                            </Button>
                            <Button
                              onClick={() => openSendDialog(quote, false)}
                              variant="outline"
                              className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
                            >
                              {t("quotes.resend", "Resend")}
                            </Button>
                            <Button
                              data-testid={`delete-pending-${quote.quote_id}`}
                              onClick={() => handleDeleteQuote(quote.quote_id)}
                              variant="outline"
                              className="border-red-400/50 text-red-400 hover:bg-red-400/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Processed Quotes Section - Paid/Converted/Accepted */}
              {processedQuotes.length > 0 && (
                <section>
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#39FF14]" />
                    {t("quotes.processed", "Processed Quotes")} ({processedQuotes.length})
                  </h2>
                  <div className="grid gap-3">
                    {processedQuotes.map((quote) => (
                      <div
                        key={quote.quote_id}
                        className="bg-[#121212] border border-[#27272A] rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="text-white font-medium">{quote.customer_name}</h3>
                            {getStatusBadge(quote.status)}
                          </div>
                          <p className="text-[#A1A1AA] text-sm mt-1">{quote.customer_email}</p>
                          <p className="text-[#A1A1AA] text-xs mt-1">
                            {yardSizeLabels[quote.yard_size]} • {grassHeightLabels[quote.grass_height]}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            {quote.quoted_price && (
                              <div className="space-y-1">
                                <p className="text-[#39FF14] font-bold">${quote.quoted_price?.toFixed(2)} <span className="text-[#A1A1AA] text-xs font-normal">one-time</span></p>
                                {quote.monthly_1x_price && (
                                  <p className="text-blue-400 text-sm">${quote.monthly_1x_price?.toFixed(2)}/mo (1x)</p>
                                )}
                                {quote.monthly_2x_price && (
                                  <p className="text-blue-300 text-sm">${quote.monthly_2x_price?.toFixed(2)}/mo (2x)</p>
                                )}
                                {quote.yearly_1x_price && (
                                  <p className="text-yellow-400 text-sm">${(quote.yearly_1x_price / 12).toFixed(2)}/mo yearly (1x)</p>
                                )}
                                {quote.yearly_2x_price && (
                                  <p className="text-yellow-300 text-sm">${(quote.yearly_2x_price / 12).toFixed(2)}/mo yearly (2x)</p>
                                )}
                              </div>
                            )}
                            {quote.quote_sent_via && (
                              <p className="text-[#A1A1AA] text-xs mt-1">Sent via {quote.quote_sent_via}</p>
                            )}
                          </div>
                          <Button
                            onClick={() => handleDeleteQuote(quote.quote_id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:bg-red-400/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>

      {/* Send/Edit Quote Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              {isEditingQuote ? (
                <>
                  <Edit className="w-5 h-5 text-blue-400" />
                  {t("quotes.editQuote", "Edit Quote")}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 text-[#39FF14]" />
                  {t("quotes.sendQuote", "Send Quote")}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="p-4 bg-[#0A0A0A] rounded-lg">
                <p className="text-white font-medium">{selectedQuote.customer_name}</p>
                <p className="text-[#A1A1AA] text-sm">{selectedQuote.customer_email}</p>
                {selectedQuote.customer_phone && (
                  <p className="text-[#A1A1AA] text-sm">{selectedQuote.customer_phone}</p>
                )}
              </div>

              {/* Quote Details */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-[#0A0A0A] rounded-lg">
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase mb-1">{t("quotes.yardSize", "Yard Size")}</p>
                  <p className="text-white">{yardSizeLabels[selectedQuote.yard_size]}</p>
                </div>
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase mb-1">{t("quotes.grassHeight", "Grass Height")}</p>
                  <p className="text-white">{grassHeightLabels[selectedQuote.grass_height]}</p>
                </div>
                {selectedQuote.customer_address && (
                  <div className="col-span-2">
                    <p className="text-[#A1A1AA] text-xs uppercase mb-1">{t("quotes.address", "Address")}</p>
                    <p className="text-white">{selectedQuote.customer_address}</p>
                  </div>
                )}
              </div>

              {/* Price Inputs - Frequency Based */}
              <div className="space-y-4">
                <p className="text-[#A1A1AA] text-sm">{t("quotes.enterPricing", "Enter pricing for each service option:")}</p>
                
                {/* One-Time Single Cut Price */}
                <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                  <Label className="text-white font-medium flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#39FF14] rounded-full"></span>
                    {t("quotes.oneTimeSingleCut", "One-Time Single Cut")} *
                  </Label>
                  <p className="text-[#A1A1AA] text-xs mb-2">{t("quotes.oneTimeCutDesc", "Price for a single grass cutting service")}</p>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#39FF14]" />
                    <Input
                      data-testid="quote-onetime-price-input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={quoteForm.oneTimePrice}
                      onChange={(e) => setQuoteForm({ ...quoteForm, oneTimePrice: e.target.value })}
                      className="pl-10 bg-[#121212] border-[#27272A] text-white text-lg font-bold focus:border-[#39FF14]"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Monthly Subscription - 1x and 2x */}
                {subscriptionsEnabled && (
                <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                  <Label className="text-white font-medium flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                    {t("quotes.monthlySubscription", "Monthly Subscription")}
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[#A1A1AA] text-xs mb-2">1 Cut/Month</p>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
                        <Input
                          data-testid="quote-monthly-1x-price-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={quoteForm.monthly1xPrice}
                          onChange={(e) => setQuoteForm({ ...quoteForm, monthly1xPrice: e.target.value })}
                          className="pl-9 bg-[#121212] border-[#27272A] text-white font-bold focus:border-blue-400"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs mb-2">2 Cuts/Month</p>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                        <Input
                          data-testid="quote-monthly-2x-price-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={quoteForm.monthly2xPrice}
                          onChange={(e) => setQuoteForm({ ...quoteForm, monthly2xPrice: e.target.value })}
                          className="pl-9 bg-[#121212] border-[#27272A] text-white font-bold focus:border-blue-300"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Yearly Subscription - 1x and 2x */}
                {subscriptionsEnabled && (
                <div className="p-4 bg-[#0A0A0A] rounded-lg border border-yellow-400/30">
                  <Label className="text-white font-medium flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                    {t("quotes.yearlySubscription", "Yearly Subscription")}
                    <span className="ml-auto text-yellow-400 text-xs font-normal">{t("landing.subscriptions.bestValue", "Best Value")}</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[#A1A1AA] text-xs mb-2">1 Cut/Month</p>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-yellow-400" />
                        <Input
                          data-testid="quote-yearly-1x-price-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={quoteForm.yearly1xPrice}
                          onChange={(e) => setQuoteForm({ ...quoteForm, yearly1xPrice: e.target.value })}
                          className="pl-9 bg-[#121212] border-[#27272A] text-white font-bold focus:border-yellow-400"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs mb-2">2 Cuts/Month</p>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-yellow-300" />
                        <Input
                          data-testid="quote-yearly-2x-price-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={quoteForm.yearly2xPrice}
                          onChange={(e) => setQuoteForm({ ...quoteForm, yearly2xPrice: e.target.value })}
                          className="pl-9 bg-[#121212] border-[#27272A] text-white font-bold focus:border-yellow-300"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Custom Message */}
              <div>
                <Label className="text-[#A1A1AA]">{t("quotes.customMessage", "Custom Message")} ({t("common.optional", "optional")})</Label>
                <Textarea
                  data-testid="quote-message-input"
                  value={quoteForm.message}
                  onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] min-h-[100px]"
                  placeholder={t("quotes.messagePlaceholder", "Leave blank to use default message...")}
                />
              </div>

              {/* Send Options */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#A1A1AA]" />
                    <span className="text-white">{t("quotes.sendViaEmail", "Send via Email")}</span>
                  </div>
                  <Switch
                    checked={quoteForm.sendEmail}
                    onCheckedChange={(checked) => setQuoteForm({ ...quoteForm, sendEmail: checked })}
                    className="data-[state=checked]:bg-[#39FF14]"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#A1A1AA]" />
                    <span className="text-white">{t("quotes.sendViaSMS", "Send via SMS")}</span>
                  </div>
                  <Switch
                    checked={quoteForm.sendSms}
                    onCheckedChange={(checked) => setQuoteForm({ ...quoteForm, sendSms: checked })}
                    className="data-[state=checked]:bg-[#39FF14]"
                  />
                </div>
              </div>

              {/* Warning if no method selected - only show for sending */}
              {!isEditingQuote && !quoteForm.sendEmail && !quoteForm.sendSms && (
                <p className="text-yellow-400 text-sm text-center">
                  ⚠️ {t("quotes.selectMethod", "Select at least one method to send the quote")}
                </p>
              )}

              {/* Info for editing mode */}
              {isEditingQuote && (
                <p className="text-blue-400 text-sm text-center">
                  💡 {t("quotes.editInfo", "Toggle email/SMS on to resend the updated quote to the customer")}
                </p>
              )}

              {/* Send/Save Button */}
              <Button
                data-testid="confirm-send-quote-btn"
                onClick={handleSendQuote}
                disabled={sendingQuote || (!isEditingQuote && !quoteForm.sendEmail && !quoteForm.sendSms) || !quoteForm.oneTimePrice}
                className={`w-full font-bold py-6 ${isEditingQuote ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-[#39FF14] text-black hover:bg-[#32D711]'}`}
              >
                {sendingQuote ? (
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : isEditingQuote ? (
                  <>
                    <Edit className="w-5 h-5 mr-2" />
                    {quoteForm.sendEmail || quoteForm.sendSms ? t("quotes.saveAndResend", "Save & Resend Quote") : t("quotes.saveChanges", "Save Changes")}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {t("quotes.sendQuote", "Send Quote")}
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert to Job Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <CalendarPlus className="w-6 h-6 text-purple-400" />
              {t("quotes.scheduleJob", "Schedule Job")}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="p-4 bg-[#0A0A0A] rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-medium">{selectedQuote.customer_name}</p>
                    <p className="text-[#A1A1AA] text-sm">{selectedQuote.customer_email}</p>
                    {selectedQuote.customer_address && (
                      <p className="text-[#A1A1AA] text-xs mt-1">{selectedQuote.customer_address}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-[#39FF14] font-bold text-lg">${selectedQuote.quoted_price?.toFixed(2)}</p>
                    <p className="text-[#A1A1AA] text-xs">{yardSizeLabels[selectedQuote.yard_size]}</p>
                  </div>
                </div>
              </div>

              {/* Schedule Date */}
              <div>
                <Label className="text-[#A1A1AA]">{t("schedule.date", "Schedule Date")} *</Label>
                <Input
                  data-testid="convert-date-input"
                  type="date"
                  value={convertForm.scheduledDate}
                  onChange={(e) => setConvertForm({ ...convertForm, scheduledDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                />
              </div>

              {/* Schedule Time */}
              <div>
                <Label className="text-[#A1A1AA]">{t("schedule.time", "Schedule Time")} *</Label>
                <Select 
                  value={convertForm.scheduledTime} 
                  onValueChange={(value) => setConvertForm({ ...convertForm, scheduledTime: value })}
                >
                  <SelectTrigger data-testid="convert-time-select" className="bg-[#0A0A0A] border-[#27272A] text-white">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    {["07:00", "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"].map((time) => (
                      <SelectItem key={time} value={time} className="text-white hover:bg-[#27272A]">
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Duration */}
              <div>
                <Label className="text-[#A1A1AA]">{t("schedule.duration", "Estimated Duration")}</Label>
                <Select 
                  value={convertForm.durationMinutes.toString()} 
                  onValueChange={(value) => setConvertForm({ ...convertForm, durationMinutes: parseInt(value) })}
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    <SelectItem value="30" className="text-white hover:bg-[#27272A]">30 minutes</SelectItem>
                    <SelectItem value="60" className="text-white hover:bg-[#27272A]">1 hour</SelectItem>
                    <SelectItem value="90" className="text-white hover:bg-[#27272A]">1.5 hours</SelectItem>
                    <SelectItem value="120" className="text-white hover:bg-[#27272A]">2 hours</SelectItem>
                    <SelectItem value="180" className="text-white hover:bg-[#27272A]">3 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Assign Staff */}
              <div>
                <Label className="text-[#A1A1AA]">{t("schedule.assignStaff", "Assign Staff")} ({t("common.optional", "Optional")})</Label>
                <Select 
                  value={convertForm.assignedStaffId || "unassigned"} 
                  onValueChange={(value) => setConvertForm({ ...convertForm, assignedStaffId: value === "unassigned" ? "" : value })}
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    <SelectItem value="unassigned" className="text-white hover:bg-[#27272A]">
                      <span className="text-[#A1A1AA]">{t("schedule.unassigned", "Unassigned")}</span>
                    </SelectItem>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.staff_id} value={staff.staff_id} className="text-white hover:bg-[#27272A]">
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4 text-[#39FF14]" />
                          {staff.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-[#A1A1AA]">{t("schedule.notes", "Notes")} ({t("common.optional", "Optional")})</Label>
                <Textarea
                  data-testid="convert-notes-input"
                  value={convertForm.notes}
                  onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] min-h-[80px]"
                  placeholder={t("schedule.notesPlaceholder", "Any special instructions for this job...")}
                />
              </div>

              {/* Convert Button */}
              <Button
                data-testid="confirm-convert-btn"
                onClick={handleConvertToJob}
                disabled={convertingQuote || !convertForm.scheduledDate || !convertForm.scheduledTime}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-6"
              >
                {convertingQuote ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <CalendarPlus className="w-5 h-5 mr-2" />
                    {t("quotes.scheduleJobFor", "Schedule Job for")} {convertForm.scheduledDate ? format(parseISO(convertForm.scheduledDate), "MMM d") : "..."}
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              {t("quotes.deleteQuote", "Delete Quote")}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-[#A1A1AA]">
              {t("quotes.deleteConfirm", "Are you sure you want to delete this quote? This action cannot be undone.")}
            </p>
            
            <div className="flex gap-3">
              <Button
                data-testid="cancel-delete-btn"
                variant="outline"
                className="flex-1 border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setQuoteToDelete(null);
                }}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                data-testid="confirm-delete-btn"
                onClick={confirmDeleteQuote}
                disabled={deletingQuote}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold"
              >
                {deletingQuote ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t("common.delete", "Delete")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activate Customer Dialog */}
      <Dialog open={activateDialogOpen} onOpenChange={setActivateDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#39FF14]" />
              Activate Customer
            </DialogTitle>
          </DialogHeader>
          
          {activateQuote && (
            <div className="space-y-4">
              <div className="p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                <p className="text-white font-semibold">{activateQuote.customer_name}</p>
                <p className="text-[#A1A1AA] text-sm">{activateQuote.email}</p>
              </div>

              <p className="text-[#A1A1AA] text-sm">Choose the service this customer wants:</p>

              <div className="space-y-2">
                {/* Single Cut */}
                <button
                  data-testid="activate-single-cut"
                  disabled={activating}
                  onClick={() => handleActivateCustomer("single_cut")}
                  className="w-full p-4 bg-[#0A0A0A] border-2 border-[#27272A] rounded-lg hover:border-[#39FF14] transition-all text-left flex items-center justify-between group"
                >
                  <div>
                    <p className="text-white font-bold">Single Cut</p>
                    <p className="text-[#A1A1AA] text-xs">One-time grass cutting service</p>
                  </div>
                  {activateQuote.quoted_price && (
                    <p className="text-[#39FF14] font-bold text-lg">${activateQuote.quoted_price.toFixed(2)}</p>
                  )}
                </button>

                {/* Monthly 1x */}
                {activateQuote.monthly_1x_price && (
                  <button
                    data-testid="activate-monthly-1x"
                    disabled={activating}
                    onClick={() => handleActivateCustomer("monthly_1x")}
                    className="w-full p-4 bg-[#0A0A0A] border-2 border-[#27272A] rounded-lg hover:border-blue-400 transition-all text-left flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white font-bold">Monthly — 1 Cut/Month</p>
                      <p className="text-[#A1A1AA] text-xs">Monthly subscription, 1 cut per month</p>
                    </div>
                    <p className="text-blue-400 font-bold text-lg">${activateQuote.monthly_1x_price.toFixed(2)}<span className="text-xs font-normal">/mo</span></p>
                  </button>
                )}

                {/* Monthly 2x */}
                {activateQuote.monthly_2x_price && (
                  <button
                    data-testid="activate-monthly-2x"
                    disabled={activating}
                    onClick={() => handleActivateCustomer("monthly_2x")}
                    className="w-full p-4 bg-[#0A0A0A] border-2 border-[#27272A] rounded-lg hover:border-blue-300 transition-all text-left flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white font-bold">Monthly — 2 Cuts/Month</p>
                      <p className="text-[#A1A1AA] text-xs">Monthly subscription, 2 cuts per month</p>
                    </div>
                    <p className="text-blue-300 font-bold text-lg">${activateQuote.monthly_2x_price.toFixed(2)}<span className="text-xs font-normal">/mo</span></p>
                  </button>
                )}

                {/* Yearly 1x */}
                {activateQuote.yearly_1x_price && (
                  <button
                    data-testid="activate-yearly-1x"
                    disabled={activating}
                    onClick={() => handleActivateCustomer("yearly_1x")}
                    className="w-full p-4 bg-[#0A0A0A] border-2 border-yellow-400/30 rounded-lg hover:border-yellow-400 transition-all text-left flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white font-bold">Yearly — 1 Cut/Month</p>
                      <p className="text-[#A1A1AA] text-xs">Annual subscription, 1 cut per month</p>
                    </div>
                    <p className="text-yellow-400 font-bold text-lg">${activateQuote.yearly_1x_price.toFixed(2)}<span className="text-xs font-normal">/yr</span></p>
                  </button>
                )}

                {/* Yearly 2x */}
                {activateQuote.yearly_2x_price && (
                  <button
                    data-testid="activate-yearly-2x"
                    disabled={activating}
                    onClick={() => handleActivateCustomer("yearly_2x")}
                    className="w-full p-4 bg-[#0A0A0A] border-2 border-yellow-400/30 rounded-lg hover:border-yellow-300 transition-all text-left flex items-center justify-between relative"
                  >
                    <span className="absolute -top-2 right-3 bg-[#39FF14] text-black text-[10px] font-bold px-2 py-0.5 rounded-full">BEST VALUE</span>
                    <div>
                      <p className="text-white font-bold">Yearly — 2 Cuts/Month</p>
                      <p className="text-[#A1A1AA] text-xs">Annual subscription, 2 cuts per month</p>
                    </div>
                    <p className="text-yellow-300 font-bold text-lg">${activateQuote.yearly_2x_price.toFixed(2)}<span className="text-xs font-normal">/yr</span></p>
                  </button>
                )}
              </div>

              {activating && (
                <div className="flex items-center justify-center gap-2 text-[#39FF14]">
                  <div className="w-4 h-4 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Activating...</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Quick Quote Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#39FF14]/30 text-white max-w-md" data-testid="create-quote-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#39FF14]" />
              Create New Quote
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA] text-sm">Label (optional)</Label>
              <Input
                value={newQuote.label}
                onChange={(e) => setNewQuote({ ...newQuote, label: e.target.value })}
                placeholder="e.g. Corner lot on 58, Large backyard..."
                className="bg-[#0A0A0A] border-[#27272A] text-white"
                data-testid="new-quote-label"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-sm">Yard Size</Label>
                <Select value={newQuote.yard_size} onValueChange={(v) => setNewQuote({ ...newQuote, yard_size: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white" data-testid="new-quote-yard">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    <SelectItem value="small" className="text-white">Small</SelectItem>
                    <SelectItem value="medium" className="text-white">Medium</SelectItem>
                    <SelectItem value="large" className="text-white">Large</SelectItem>
                    <SelectItem value="extra_large" className="text-white">Extra Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-sm">Grass Height</Label>
                <Select value={newQuote.grass_height} onValueChange={(v) => setNewQuote({ ...newQuote, grass_height: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white" data-testid="new-quote-grass">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    <SelectItem value="short" className="text-white">Short</SelectItem>
                    <SelectItem value="medium" className="text-white">Medium</SelectItem>
                    <SelectItem value="tall" className="text-white">Tall</SelectItem>
                    <SelectItem value="overgrown" className="text-white">Overgrown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t border-[#27272A] pt-3">
              <p className="text-[#A1A1AA] text-xs uppercase font-semibold mb-3">Pricing</p>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-[#A1A1AA] text-sm">One-Time Price ($)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#39FF14]" />
                    <Input type="number" value={newQuote.quoted_price} onChange={(e) => setNewQuote({ ...newQuote, quoted_price: e.target.value })} placeholder="0.00" className="pl-9 bg-[#0A0A0A] border-[#27272A] text-white" data-testid="new-quote-price" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[#A1A1AA] text-xs">Monthly 1x/mo</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#39FF14]/60" />
                      <Input type="number" value={newQuote.monthly_1x_price} onChange={(e) => setNewQuote({ ...newQuote, monthly_1x_price: e.target.value })} placeholder="0.00" className="pl-8 bg-[#0A0A0A] border-[#27272A] text-white text-sm" data-testid="new-quote-m1x" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#A1A1AA] text-xs">Monthly 2x/mo</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#39FF14]/60" />
                      <Input type="number" value={newQuote.monthly_2x_price} onChange={(e) => setNewQuote({ ...newQuote, monthly_2x_price: e.target.value })} placeholder="0.00" className="pl-8 bg-[#0A0A0A] border-[#27272A] text-white text-sm" data-testid="new-quote-m2x" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[#A1A1AA] text-xs">Yearly 1x/mo</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#39FF14]/60" />
                      <Input type="number" value={newQuote.yearly_1x_price} onChange={(e) => setNewQuote({ ...newQuote, yearly_1x_price: e.target.value })} placeholder="0.00" className="pl-8 bg-[#0A0A0A] border-[#27272A] text-white text-sm" data-testid="new-quote-y1x" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#A1A1AA] text-xs">Yearly 2x/mo</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#39FF14]/60" />
                      <Input type="number" value={newQuote.yearly_2x_price} onChange={(e) => setNewQuote({ ...newQuote, yearly_2x_price: e.target.value })} placeholder="0.00" className="pl-8 bg-[#0A0A0A] border-[#27272A] text-white text-sm" data-testid="new-quote-y2x" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA] text-sm">Notes (optional)</Label>
              <Textarea
                value={newQuote.notes}
                onChange={(e) => setNewQuote({ ...newQuote, notes: e.target.value })}
                placeholder="Internal notes about this quote..."
                className="bg-[#0A0A0A] border-[#27272A] text-white h-16"
                data-testid="new-quote-notes"
              />
            </div>
            <Button
              onClick={handleCreateQuote}
              disabled={creatingQuote}
              className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
              data-testid="save-new-quote-btn"
            >
              {creatingQuote ? "Creating..." : "Create Quote"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Quotes;
